import * as GraphicsLayer from './GraphicsLayer'
import type { GraphicsLayerHandle, GraphicHandle } from './GraphicsLayer'
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol"
import { parcelFeatureToParcelData } from "../../parcels/hooks"
import { SearchResult } from '../../search/types'
import * as SearchResultsLayer from './SearchResultsLayer'


export type SelectionSource = "search" | "map-click"

export interface Selection {
  source: SelectionSource
  handle: GraphicHandle
  PIN: string
  label: string
  subLabel: string
  geometry: __esri.Geometry
}

function featureToSelection(handle: GraphicHandle, feature: __esri.Graphic): Selection {
  return {
    source: "map-click",
    handle,
    PIN: feature.attributes.PIN,
    label: feature.attributes.OwnerName,
    subLabel: feature.attributes.LegalDescr,
    geometry: feature.geometry,
  }
}

let layerHandle: GraphicsLayerHandle = GraphicsLayer.NULL_LAYER_HANDLE
let selectedParcels: Selection[] = []
let focusedSelection: Selection | null = null
let currentSelectionIndex: number | null = null
let isMapSelectable = true

const selectedSymbol = new SimpleFillSymbol({
  color: [ 1, 222, 154, 0],
  style: "solid",
  outline: { // autocasts as new SimpleLineSymbol()
    color: [1, 222, 154],
    width: 4
  }
})


const focusedSelectionSymbol = new SimpleFillSymbol({
  color:  [ 0, 193, 240, 0],
  style: "solid",
  outline: { // autocasts as new SimpleLineSymbol()
    color: [0, 193, 240],
    width: 6
  }
})

export const getSelectedParcel = () => focusedSelection
export const getSelectedParcelCount = () => selectedParcels.length
export const getCurrentSelectionIndex = () => currentSelectionIndex
export const getIsMapSelectable = () => isMapSelectable
export const setIsMapSelectable = (value: boolean) => isMapSelectable = value

export const initSelectionLayer = (map: __esri.Map, view: __esri.MapView) => {
  layerHandle = GraphicsLayer.createGraphicsLayer(map, view)
}

const createGraphicFromFeature = (feature: __esri.Graphic): GraphicHandle => {
  const data = parcelFeatureToParcelData(feature.attributes)
  const handle = GraphicsLayer.createGraphic(layerHandle, data, feature.geometry, selectedSymbol)
  return handle
}

const createFocusedGraphicFromSearchResult = (result: SearchResult): GraphicHandle => {
  const geometry = SearchResultsLayer.getGeometry(result.handle)
  if (!geometry) return GraphicsLayer.NULL_GRAPHIC_HANDLE
  const handle = GraphicsLayer.createGraphic(layerHandle, result, geometry, focusedSelectionSymbol)
  return handle
}

const createFocusedGraphicFromSelection = (selection: Selection): GraphicHandle => {
  const handle = GraphicsLayer.createGraphic(layerHandle, selection, selection.geometry, focusedSelectionSymbol)
  return handle
}

export const selectSearchResult = (result: SearchResult) => {
  if (layerHandle === GraphicsLayer.NULL_LAYER_HANDLE) return
  clearGraphics()
  const handle = createFocusedGraphicFromSearchResult(result)
  if (handle === GraphicsLayer.NULL_GRAPHIC_HANDLE) return
  GraphicsLayer.addGraphic(layerHandle, handle)
  const geometry = SearchResultsLayer.getGeometry(result.handle)
  if (!geometry) return;
  focusedSelection = {
    source: "search",
    handle,
    PIN: result.type === "Address" ? "" : result.PIN,
    label: result.label,
    subLabel: result.type === "Address" ? "" : result.subLabel,
    geometry,
  }
}

export const addClickedFeatures = (features: __esri.Graphic[]): void => { 
  if (layerHandle === GraphicsLayer.NULL_LAYER_HANDLE) return
  clearGraphics()
  
  let graphicHandles = []
  selectedParcels = []
  for (let i = 0; i < features.length; i++) {
    const feature = features[i]
    const handle = createGraphicFromFeature(feature)
    graphicHandles.push(handle)
    selectedParcels.push(featureToSelection(handle, feature))
  }
  GraphicsLayer.addGraphics(layerHandle, graphicHandles)
  
  setFocusedIndex(0)
  currentSelectionIndex = 0
}

export const selectIfNoSelection = (feature: __esri.Graphic): void => {
  if (feature && selectedParcels.length === 0) {
    addClickedFeatures([feature])
  }
}


function setFocusedIndex(index: number) {
  if (selectedParcels.length === 0 || index < 0 || index >= selectedParcels.length) return
  const selection = selectedParcels[index]
  setFocusedSelection(selection)
  currentSelectionIndex = index
}

function setFocusedSelection(selection: Selection) {
  clearFocus()
  focusedSelection = { ...selection }
  const handle = createFocusedGraphicFromSelection(focusedSelection)
  focusedSelection.handle = handle
  GraphicsLayer.addGraphic(layerHandle, handle)
}

export const setCurrentIndex = (index: number) => {
  if (selectedParcels.length === 0) return
  if (currentSelectionIndex !== index && index >= 0 && index < selectedParcels.length) {
    clearFocus()
    const handle = createFocusedGraphicFromSelection(selectedParcels[index])
    focusedSelection = selectedParcels[index]
    focusedSelection.handle = handle
    GraphicsLayer.addGraphic(layerHandle, handle)
    currentSelectionIndex = index
  }
}

export const selectParcels = (searchResults: SearchResult[], index: number = 0) => {
  clearGraphics()
  currentSelectionIndex = index
}

export const clearFocus = () => {
  if (layerHandle === GraphicsLayer.NULL_LAYER_HANDLE) return
  if (focusedSelection) {
    GraphicsLayer.removeGraphic(layerHandle, focusedSelection.handle)
    focusedSelection = null
  }
}

export const clearGraphics = () => {
  if (layerHandle === GraphicsLayer.NULL_LAYER_HANDLE) return
  GraphicsLayer.clearGraphics(layerHandle)
  selectedParcels = []
  focusedSelection = null
  currentSelectionIndex = null
}